import React, { useState, useEffect } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ART_FUND_DETAILS } from './../../utils/config';
import DefaultFundHeader from './DefaultFundHeader';
import SelectContainer from './../../components/Fieldsets/SelectContainer';
import Button from './../../components/Button/Button';
import WhySelectDefaultFund from './WhySelectDefaultFund';
import RegisteringWithART from '../../components/RegisteringWithART/RegisteringWithART';
import {
  setARTSelected,
  setEmployerRegisterSuper,
  setSelectedFunds
} from '../../store/actions/globalActions';
import { handleReturnToPayroll } from '../../store/thunks/global';
import SearchApi from '../../store/api/search';
import { saveParticipantData } from '../../store/actions/participantActions';
import ClearningHouseApi from '../../store/api/clearningHouse';
import ValidationPanel from './../../components/ValidationPanel/ValidationPanel';
import { filterByEndDate } from './../../utils/funds';
import { isActive } from './../../utils/date';

const DefaultFund = props => {
  const dispatch = useDispatch();
  const { participant, validations, payrollName, redirectUrl } = useSelector(
    state => state.participant.data
  );
  const [validationErrors, setValidationErrors] = useState(validations);
  const { employerRegisterSuper, funds, isARTSelected } = useSelector(
    state => state.global.data
  );
  const fundsToDisplay =
    Array.isArray(funds) && funds.length > 0 ? filterByEndDate(funds) : [];

  // A fund without a USI is invalid and cannot be submitted, so it must not
  // enable navigation. Only funds that carry a USI count towards "Next".
  const hasValidFundToDisplay = fundsToDisplay.some(fund => fund.usi);

  const [isValidating, setValitating] = useState(false);

  useEffect(
    () => {
      if (participant) {
        // Set the filtered validation errors
        setValidationErrors(validations);
      }
    },
    [participant, validations]
  );

  const handleChange = e => {
    const value = e.target.checked;
    dispatch(setEmployerRegisterSuper(value));
  };

  // Update defaultFunds to the non-bureau participant/employer - used in "DefaultFund" component
  const saveDefaultFundsForNonBureau = async defaultFunds => {
    const newParticipant = {
      ...participant,
      employers: [{ ...participant.employers[0], defaultFunds }]
    };
    try {
      setValitating(true);
      const res = await ClearningHouseApi.updateParticipant(newParticipant);

      dispatch(
        saveParticipantData({
          participant: res.processed ? res.participantDetail : newParticipant, // Update the participant with the new default funds if not processed, otherwise use the participantDetail from the response
          validations: res.validations
        })
      );

      if (res.validations.length === 0) {
        // Navigate to next step
        props.history.push({
          pathname: '/t&c',
          hash: props.location.hash
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setValitating(false);
    }
  };

  const handleClick = () => {
    const validFundsToUpdate = funds.filter(fundItem => {
      const hasUsi = !!fundItem.usi; // funds without a USI are invalid and must never be sent to the BE
      const hasEnd = !!fundItem.endDate;
      const hasProduct = !!fundItem.productName; // valid fund should have productName
      const isExisting = // existing fund should have defaultFundId
        fundItem.defaultFundId != null &&
        fundItem.defaultFundId !== '' &&
        Number(fundItem.defaultFundId) >= 0;

      return hasUsi && ((!isExisting && hasProduct && !hasEnd) || isExisting); // new valid fund without end date, or existing fund (regardless of end date); invalid (no USI) funds are excluded
    });

    saveDefaultFundsForNonBureau(validFundsToUpdate);
  };

  const getAllDefaultFundDetails = defaultFunds => {
    const USIs = defaultFunds.map(defaultFundItem => defaultFundItem.usi);
    if (USIs.length > 0) {
      // get funds details
      const fundDetailPromises = USIs.map(usi => {
        if (!usi) {
          // don't look up funds without a USI; keep them so they stay visible and removable
          return Promise.resolve({ uniqueSuperannuationIdentifier: usi });
        }
        return SearchApi.getFundDetails('usi', usi)
          .then(
            fundsInfo =>
              fundsInfo[0]
                ? fundsInfo[0]
                : { uniqueSuperannuationIdentifier: usi } // still return usi even if not found
          )
          .catch(err => {
            console.warn(err);
            return null;
          });
      });

      Promise.all(fundDetailPromises).then(results => {
        const mappedFunds = results.map(fundResult => {
          const { defaultFundId, endDate, startDate } = defaultFunds.find(
            defaultFundItem =>
              defaultFundItem.usi === fundResult.uniqueSuperannuationIdentifier
          );

          return {
            usi: fundResult.uniqueSuperannuationIdentifier,
            australianBusinessNumber: fundResult.australianBusinessNumber,
            productName: fundResult.productName,
            defaultFundId,
            endDate,
            startDate
          };
        });

        dispatch(
          // update the global funds list with funds details
          setSelectedFunds(mappedFunds)
        );
      });
    }
  };

  useEffect(() => {
    document.title = `Beam - Default fund`;
  }, []);

  useEffect(
    // set isARTSelected
    () => {
      const hasARTSelected =
        funds &&
        funds.length > 0 &&
        funds
          .filter(fund => !fund.endDate || isActive(fund.endDate))
          .map(selectedFund => selectedFund.usi)
          .indexOf(ART_FUND_DETAILS.uniqueSuperannuationIdentifier) > -1;
      dispatch(setARTSelected(hasARTSelected));
    },
    [funds]
  );

  useEffect(
    () => {
      if (
        // get default funds details
        participant &&
        Array.isArray(participant.employers) &&
        participant.employers.length > 0 &&
        Array.isArray(participant.employers[0].defaultFunds) &&
        participant.employers[0].defaultFunds.length > 0
      ) {
        if (Array.isArray(funds) && funds.length > 0) return; // return if already initialized

        const defaultFunds = participant.employers[0].defaultFunds;

        getAllDefaultFundDetails(defaultFunds);
      }
    },
    [participant]
  );

  return (
    <div>
      {validationErrors &&
        validationErrors.length > 0 && (
          <ValidationPanel errors={validationErrors} />
        )}
      <section
        className="Section Section--leading"
        style={{ marginBottom: '2rem' }}
      >
        <DefaultFundHeader />
        <WhySelectDefaultFund />
      </section>
      <SelectContainer fundsToDisplay={fundsToDisplay} />
      {funds &&
        funds.length > 0 &&
        !isARTSelected && (
          <section className="Section">
            <RegisteringWithART
              employerRegisterART={employerRegisterSuper}
              onChange={handleChange}
            />
          </section>
        )}

      <div style={{ marginBottom: '3rem' }}>
        Before you make any decision about any financial products, always do
        your research. Read the relevant Product Disclosure Statement (PDS) and
        Target Market Determination (TMD). For a copy of the PDS and TMD for
        Super Savings products, you can find them at art.com.au/pds. Australian
        Retirement Trust’s Super Savings products are issued by Australian
        Retirement Trust Pty Ltd (ABN 88 010 720 840, AFSL No. 228975) as
        trustee for Australian Retirement Trust (ABN 60 905 115 063)
      </div>
      <section className="Section PageNavGroup">
        <Button
          type="button"
          primary
          hollow
          customclass="Button--cancel"
          disabled={isValidating}
          onClick={() => {
            dispatch(handleReturnToPayroll(payrollName, redirectUrl));
          }}
        >
          <span>Cancel</span>
        </Button>
        {!isValidating && (
          <Link
            className="Button Button--primary Button--hollow Button--prev"
            to={{
              pathname: '/payment-method',
              hash: props.location.hash
            }}
          >
            <span>Back</span>
          </Link>
        )}
        <Button
          primary
          next={!isValidating}
          disabled={!hasValidFundToDisplay || isValidating}
          title="Please select at least one default fund before proceed to the next step"
          onClick={handleClick}
        >
          <span>{isValidating ? 'Validating...' : 'Next'}</span>
        </Button>
      </section>
    </div>
  );
};

export default withRouter(props => <DefaultFund {...props} />);
